import React from "react";

import { Checkbox } from "@replo/design-system/components/checkbox/Checkbox";
import { Tag } from "@replo/design-system/components/tag/Tag";
import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import twMerge from "@replo/design-system/utils/twMerge";
import { ConcentricRingLogo } from "@v2/components/ConcentricRingLogo";
import { animated } from "react-spring";

type SetupOption = "new" | "import" | "ai" | "themeBuilder" | "import-later";

interface ProductHostingTypeSelectorProps {
  selectedHostingType: SetupOption | null;
  setSelectedHostingType: (option: SetupOption) => void;
  setSelectedProduct?: (option: SetupOption) => void;
  isShopContext?: boolean;
  slideUpAnimation: any;
  hideSetupShopifyLaterOption?: boolean;
  className?: string;
  showDefaultHeader?: boolean;
  size?: "base" | "sm";
}

const ProductHostingTypeSelector: React.FC<ProductHostingTypeSelectorProps> = ({
  selectedHostingType,
  setSelectedHostingType,
  setSelectedProduct,
  isShopContext = false,
  slideUpAnimation,
  hideSetupShopifyLaterOption = false,
  className = "",
  showDefaultHeader = true,
  size = "base",
}) => {
  const isMobile = useIsMobile();

  return (
    <animated.div
      style={slideUpAnimation}
      className="flex flex-col w-full gap-3"
    >
      {showDefaultHeader && (
        <div className="text-md font-medium text-start">
          Where do you want your products to live?
        </div>
      )}
      <div
        className={twMerge(
          "flex flex-col md:flex-row gap-4 md:gap-8",
          className,
        )}
      >
        <div
          className={twMerge(
            "flex-1 min-h-40 border rounded-xl p-4 group cursor-pointer overflow-hidden relative transition-colors duration-300",
            selectedHostingType === "new"
              ? "border-transparent"
              : "border-border",
          )}
          onClick={() => {
            setSelectedHostingType("new");
            setSelectedProduct?.("ai");
          }}
        >
          <img
            src="/images/onboarding/step3-bg.png"
            alt=""
            className={twMerge(
              "absolute inset-0 w-full h-full object-cover rounded-xl transition-opacity duration-300",
              selectedHostingType === "new" ? "opacity-100" : "opacity-0",
            )}
          />
          <div className="flex flex-col justify-center h-full relative z-10">
            <div
              className={twMerge(
                "flex flex-col gap-1 max-w-[50%] pr-4",
                isShopContext && "!text-left",
              )}
              {...(isShopContext && { "data-card-content": true })}
            >
              <div
                className={twMerge(
                  "text-base font-medium transition-colors duration-300 flex items-center gap-2",
                  selectedHostingType === "new" && "text-muted",
                )}
                style={
                  selectedHostingType === "new" ? { color: "#F5FAD9" } : {}
                }
              >
                <span>Sell with Replo</span>
                <Tag variant="beta" size="base">
                  Beta
                </Tag>
              </div>
              <div
                className={twMerge(
                  "font-light transition-colors duration-300",
                  selectedHostingType === "new" && "text-placeholder",
                  size === "sm" && "max-w-[200px]",
                )}
                style={
                  selectedHostingType === "new" ? { color: "#F5FAD9" } : {}
                }
              >
                Create products and sell them directly through Replo
              </div>
            </div>
            <div className="absolute -bottom-105 left-130 right-0 flex items-end justify-end pointer-events-none">
              <img
                src="/images/onboarding/sell-on-replo.svg"
                className={twMerge(
                  "w-[600px] h-[550px] max-w-none max-h-none object-contain transform translate-x-80 -translate-y-15 transition-transform duration-400 ease-in-out group-hover:-translate-y-18",
                  isMobile ? "translate-x-64" : "translate-x-80",
                )}
              />
            </div>
          </div>
        </div>
        <div
          className={twMerge(
            "flex-1 min-h-40 border rounded-xl p-4 group cursor-pointer overflow-hidden relative transition-colors duration-300",
            selectedHostingType === "import"
              ? "border-transparent"
              : "border-border",
          )}
          onClick={() => setSelectedHostingType("import")}
        >
          <img
            src="/images/onboarding/step3-bg.png"
            alt=""
            className={twMerge(
              "absolute inset-0 w-full h-full object-cover rounded-xl transition-opacity duration-300",
              selectedHostingType === "import" ? "opacity-100" : "opacity-0",
            )}
          />
          <ConcentricRingLogo
            variant="shopify"
            isSelected={selectedHostingType === "import"}
            hoverDirection="right"
            className="absolute -bottom-11.5 w-60 h-60 -right-20 md:-right-16"
          />
          {size === "base" && (
            <ConcentricRingLogo
              variant="replo"
              isSelected={selectedHostingType === "import"}
              hoverDirection="left"
              className="absolute -bottom-11.5 w-60 h-60 right-16 hidden md:block"
            />
          )}
          <div className="flex flex-col justify-center gap-6 h-full relative z-10">
            <div
              className={twMerge(
                "flex flex-col gap-1 pr-4",
                isMobile ? "max-w-[55%]" : "max-w-[45%]",
                isShopContext && "!text-left",
              )}
              {...(isShopContext && { "data-card-content": true })}
            >
              <div
                className={twMerge(
                  "text-base font-medium transition-colors duration-300",
                  selectedHostingType === "import" && "text-muted",
                )}
                style={
                  selectedHostingType === "import" ? { color: "#F5FAD9" } : {}
                }
              >
                Import Products from Shopify
              </div>
              <div
                className={twMerge(
                  "font-light transition-colors duration-300",
                  size === "sm" && "max-w-[200px]",
                  selectedHostingType === "import" && "text-placeholder",
                )}
                style={
                  selectedHostingType === "import" ? { color: "#F5FAD9" } : {}
                }
              >
                Import products and checkout with an existing Shopify store
              </div>
            </div>
          </div>
        </div>
      </div>
      {!hideSetupShopifyLaterOption && (
        <div className="flex justify-end">
          <div
            className="flex items-start gap-2 cursor-pointer"
            onClick={() => setSelectedHostingType("import-later")}
          >
            <Checkbox
              checked={selectedHostingType === "import-later"}
              onCheckedChange={() => setSelectedHostingType("import-later")}
            />
            <span className="text-xs font-medium text-muted leading-none mt-0.5">
              Integrate with Shopify later
            </span>
          </div>
        </div>
      )}
    </animated.div>
  );
};

export default ProductHostingTypeSelector;
